import { useState, useRef, useEffect } from "react";
import { siteData, WhatsAppSettings, LiveChatSettings, ChatSchedule, DaySchedule } from "@/store/siteData";
import { uploadFile } from "@/utils/apiClient";
import { playAdminNotificationGlobal } from "@/pages/kontrol-paneli/page";

// ── Bildirim Sesi Ayarları ────────────────────────────────────────────────────

const TONE_OPTIONS = [
  { id: "double-ding", label: "Çift Ding", desc: "Varsayılan — iki kısa ses" },
  { id: "single-ding", label: "Tek Ding", desc: "Sadece bir ding" },
  { id: "high-ping", label: "Yüksek Ping", desc: "Keskin, yüksek frekans" },
  { id: "low-boop", label: "Alçak Boop", desc: "İki kademeli alçak ton" },
  { id: "chime", label: "Melodik Chime", desc: "Üç nota yukarı doğru" },
];

function NotificationSoundSection() {
  const [settings, setSettings] = useState(() => {
    const raw = localStorage.getItem("ygNotifSettings");
    return raw ? JSON.parse(raw) : { tone: "double-ding", volume: 0.25 };
  });
  const [saved, setSaved] = useState(false);

  const update = (key: string, val: unknown) =>
    setSettings((p: Record<string, unknown>) => ({ ...p, [key]: val }));

  const handleSave = () => {
    localStorage.setItem("ygNotifSettings", JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const testSound = () => {
    // Geçici olarak kaydet, test et
    localStorage.setItem("ygNotifSettings", JSON.stringify(settings));
    playAdminNotificationGlobal();
  };

  const volumePercent = Math.round((settings.volume ?? 0.25) * 100);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/15 flex-shrink-0">
          <i className="ri-notification-3-line text-amber-400 text-xl"></i>
        </div>
        <div>
          <h2 className="text-white font-semibold text-base">Bildirim Sesi</h2>
          <p className="text-[#7B8FAF] text-xs">Yeni destek talebi geldiğinde çalan ses</p>
        </div>
      </div>

      {/* Ses Seviyesi */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[#7B8FAF] text-xs uppercase tracking-widest">Ses Seviyesi</label>
          <span className="text-white text-sm font-medium">%{volumePercent}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 flex items-center justify-center text-[#5E7398]">
            <i className="ri-volume-mute-line text-sm"></i>
          </div>
          <input
            type="range" min="0" max="1" step="0.05"
            value={settings.volume ?? 0.25}
            onChange={e => update("volume", parseFloat(e.target.value))}
            className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-[#3B82F6] bg-[#2B3654]"
          />
          <div className="w-5 h-5 flex items-center justify-center text-[#5E7398]">
            <i className="ri-volume-up-line text-sm"></i>
          </div>
        </div>
        {volumePercent === 0 && (
          <p className="text-amber-400/70 text-xs mt-1.5">Ses tamamen kapalı — bildirim çalmaz.</p>
        )}
      </div>

      {/* Ton Seçimi */}
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Bildirim Tonu</label>
        <div className="space-y-2">
          {TONE_OPTIONS.map(t => (
            <button
              key={t.id}
              onClick={() => update("tone", t.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer text-left ${
                settings.tone === t.id
                  ? "bg-[#3B82F6]/20 border-[#3B82F6]/60 text-white"
                  : "border-[#2B3654] text-[#7B8FAF] hover:text-white hover:border-[#3B82F6]/30 bg-[#0F1729]"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                {settings.tone === t.id
                  ? <i className="ri-radio-button-fill text-[#3B82F6]"></i>
                  : <i className="ri-radio-button-line text-[#5E7398]"></i>
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{t.label}</p>
                <p className="text-xs text-[#5E7398] mt-0.5">{t.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Test + Kaydet */}
      <div className="flex gap-3">
        <button onClick={testSound}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-[#3B82F6]/40 text-[#A5B5CC] text-sm rounded-lg hover:bg-[#3B82F6]/10 cursor-pointer whitespace-nowrap transition-colors">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-play-line text-sm"></i>
          </div>
          Sesi Test Et
        </button>
        <button onClick={handleSave}
          className={`flex-1 py-2.5 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${
            saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"
          }`}>
          {saved ? "✓ Kaydedildi" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

// ── WhatsApp Settings ────────────────────────────────────────────────────────

function WhatsAppSection() {
  const [data, setData] = useState<WhatsAppSettings>(() => siteData.getWhatsApp());
  const [saved, setSaved] = useState(false);

  const upd = (key: keyof WhatsAppSettings, val: unknown) =>
    setData(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    await siteData.saveWhatsApp(data);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: "rgba(37, 211, 102, 0.15)" }}>
            <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: "#25D366" }}>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>
          <div>
            <h2 className="text-white font-semibold text-base">WhatsApp Butonu</h2>
            <p className="text-[#7B8FAF] text-xs">Ziyaretçiler WhatsApp üzerinden ulaşsın</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#7B8FAF] text-xs">{data.enabled ? "Aktif" : "Kapalı"}</span>
          <button
            onClick={() => upd("enabled", !data.enabled)}
            className={`relative w-11 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${data.enabled ? "bg-green-500" : "bg-[#2B3654]"}`}
          >
            <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all" style={{ left: data.enabled ? "22px" : "2px" }}></span>
          </button>
        </div>
      </div>

      <div className={`space-y-4 transition-opacity ${data.enabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">
            WhatsApp Numarası
            <span className="text-[#5E7398] normal-case ml-2 font-normal">(ülke kodu dahil, sadece rakamlar)</span>
          </label>
          <input type="text" value={data.phone} onChange={e => upd("phone", e.target.value.replace(/\D/g, ""))}
            placeholder="905551234567"
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Varsayılan Mesaj</label>
          <textarea value={data.message} onChange={e => upd("message", e.target.value)} rows={2}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] resize-none" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Tooltip Yazısı</label>
          <input type="text" value={data.label} onChange={e => upd("label", e.target.value)}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Konum</label>
          <div className="flex gap-3">
            {(["bottom-right", "bottom-left"] as const).map(pos => (
              <button key={pos} onClick={() => upd("position", pos)}
                className={`flex-1 py-2.5 text-sm rounded-lg border transition-all cursor-pointer whitespace-nowrap ${data.position === pos ? "bg-[#3B82F6]/20 border-[#3B82F6]/60 text-white" : "border-[#2B3654] text-[#7B8FAF] hover:text-white"}`}>
                <i className={`${pos === "bottom-right" ? "ri-layout-right-2-line" : "ri-layout-left-2-line"} mr-2`}></i>
                {pos === "bottom-right" ? "Sağ Alt" : "Sol Alt"}
              </button>
            ))}
          </div>
        </div>
        {data.enabled && (
          <div className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}>
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div>
              <p className="text-white text-xs font-medium">{data.label}</p>
              <p className="text-[#7B8FAF] text-xs">wa.me/{data.phone}</p>
            </div>
          </div>
        )}
      </div>

      <button onClick={handleSave}
        className={`w-full py-2.5 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
        {saved ? "✓ Kaydedildi" : "WhatsApp Ayarlarını Kaydet"}
      </button>
    </div>
  );
}

// ── Chat Schedule (Otomatik Saat Ayarı) ──────────────────────────────────────

const DAY_LABELS: { key: keyof ChatSchedule['days']; label: string; short: string }[] = [
  { key: 'mon', label: 'Pazartesi', short: 'Pzt' },
  { key: 'tue', label: 'Salı',      short: 'Sal' },
  { key: 'wed', label: 'Çarşamba',  short: 'Çar' },
  { key: 'thu', label: 'Perşembe',  short: 'Per' },
  { key: 'fri', label: 'Cuma',      short: 'Cum' },
  { key: 'sat', label: 'Cumartesi', short: 'Cmt' },
  { key: 'sun', label: 'Pazar',     short: 'Paz' },
];

function ChatScheduleSection() {
  const [data, setData] = useState<ChatSchedule>(() => siteData.getChatSchedule());
  const [saved, setSaved] = useState(false);
  const [liveStatus, setLiveStatus] = useState(() => siteData.isChatOnlineNow());

  useEffect(() => {
    const t = setInterval(() => setLiveStatus(siteData.isChatOnlineNow()), 30000);
    return () => clearInterval(t);
  }, []);

  const updDay = (key: keyof ChatSchedule['days'], patch: Partial<DaySchedule>) =>
    setData(p => ({ ...p, days: { ...p.days, [key]: { ...p.days[key], ...patch } } }));

  const handleSave = async () => {
    await siteData.saveChatSchedule(data);
    setLiveStatus(siteData.isChatOnlineNow());
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const nowDayKey = (() => {
    const tzStr = new Date().toLocaleString('en-US', { timeZone: data.timezone || 'Europe/Istanbul' });
    const d = new Date(tzStr);
    const keys: (keyof ChatSchedule['days'])[] = ['sun','mon','tue','wed','thu','fri','sat'];
    return keys[d.getDay()];
  })();

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-500/15 flex-shrink-0">
          <i className="ri-time-line text-purple-400 text-xl"></i>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-white font-semibold text-base">Otomatik Çevrimici / Çevrimdışı</h2>
          <p className="text-[#7B8FAF] text-xs">Belirlediğiniz saatlerde destek otomatik açılır ve kapanır</p>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${
          data.autoSchedule
            ? (liveStatus ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : 'text-red-400 bg-red-500/10 border-red-500/30')
            : 'text-[#7B8FAF] bg-[#141E35] border-[#2B3654]'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            data.autoSchedule ? (liveStatus ? 'bg-emerald-400' : 'bg-red-400') : 'bg-[#5E7398]'
          }`}></span>
          {data.autoSchedule ? (liveStatus ? 'Şu an Çevrimici' : 'Şu an Kapalı') : 'Otomasyon Kapalı'}
        </div>
      </div>

      {/* Ana Toggle */}
      <div className="flex items-center justify-between bg-[#0F1729] border border-[#2B3654] rounded-xl px-4 py-3">
        <div>
          <p className="text-white text-sm font-medium">Otomatik zamanlama</p>
          <p className="text-[#7B8FAF] text-xs mt-0.5">Açıksa saat çizelgesine göre widget otomatik açılır/kapanır</p>
        </div>
        <button
          onClick={() => setData(p => ({ ...p, autoSchedule: !p.autoSchedule }))}
          className={`relative w-11 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${
            data.autoSchedule ? 'bg-purple-500' : 'bg-[#2B3654]'
          }`}
        >
          <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all"
            style={{ left: data.autoSchedule ? '22px' : '2px' }}></span>
        </button>
      </div>

      {/* Gün Tablosu */}
      <div className={`space-y-2 transition-opacity ${
        data.autoSchedule ? 'opacity-100' : 'opacity-40 pointer-events-none'
      }`}>
        {DAY_LABELS.map(({ key, label }) => {
          const day = data.days[key];
          const isToday = key === nowDayKey;
          return (
            <div key={key}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                isToday ? 'border-purple-500/40 bg-purple-500/5' : 'border-[#2B3654] bg-[#0F1729]'
              }`}
            >
              <div className="flex items-center gap-3 sm:w-32 flex-shrink-0">
                <button
                  onClick={() => updDay(key, { enabled: !day.enabled })}
                  className={`relative w-9 h-5 rounded-full transition-all cursor-pointer flex-shrink-0 ${
                    day.enabled ? 'bg-purple-500' : 'bg-[#2B3654]'
                  }`}
                >
                  <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all"
                    style={{ left: day.enabled ? '18px' : '2px' }}></span>
                </button>
                <span className={`text-sm font-medium whitespace-nowrap ${
                  day.enabled ? 'text-white' : 'text-[#5E7398]'
                }`}>
                  {label}
                  {isToday && <span className="ml-1.5 text-xs text-purple-400">(Bugün)</span>}
                </span>
              </div>
              <div className={`flex items-center gap-2 flex-1 transition-opacity ${
                day.enabled ? 'opacity-100' : 'opacity-30 pointer-events-none'
              }`}>
                <div className="flex items-center gap-2">
                  <span className="text-[#7B8FAF] text-xs whitespace-nowrap">Açılış:</span>
                  <input
                    type="time"
                    value={day.start}
                    onChange={e => updDay(key, { start: e.target.value })}
                    className="bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-2 py-1.5 outline-none focus:border-purple-500 cursor-pointer"
                  />
                </div>
                <span className="text-[#5E7398] text-sm">–</span>
                <div className="flex items-center gap-2">
                  <span className="text-[#7B8FAF] text-xs whitespace-nowrap">Kapat: </span>
                  <input
                    type="time"
                    value={day.end}
                    onChange={e => updDay(key, { end: e.target.value })}
                    className="bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-2 py-1.5 outline-none focus:border-purple-500 cursor-pointer"
                  />
                </div>
              </div>
              {!day.enabled && (
                <span className="text-xs text-[#465886] whitespace-nowrap">Kapalı gün</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
        <i className="ri-information-line text-amber-400 text-sm flex-shrink-0 mt-0.5"></i>
        <p className="text-amber-300/80 text-xs leading-relaxed">
          Saat çizelgesi <strong>Türkiye saatine (Europe/Istanbul)</strong> göre çalışır.
          Otomasyon aktifken widget’ın "Aktif" ayarı korunmalıdır; zamanlama onu otomatik override eder.
        </p>
      </div>

      <button onClick={handleSave}
        className={`w-full py-2.5 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${
          saved ? 'bg-emerald-500 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'
        }`}>
        {saved ? '✓ Kaydedildi' : 'Zamanlama Ayarlarını Kaydet'}
      </button>
    </div>
  );
}

// ── Live Chat Settings ───────────────────────────────────────────────────────

function LiveChatSection() {
  const [data, setData] = useState<LiveChatSettings>(() => siteData.getLiveChat());
  const [saved, setSaved] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const upd = (key: keyof LiveChatSettings, val: unknown) =>
    setData(p => ({ ...p, [key]: val }));

  const handleAgentPhotoUpload = async (file: File) => {
    setUploadingPhoto(true);
    try {
      const result = await uploadFile(file);
      upd("agentImageUrl", result.url);
    } catch (e) {
      console.error("Fotoğraf yüklenemedi:", e);
    }
    setUploadingPhoto(false);
  };

  const handleSave = async () => {
    await siteData.saveLiveChat(data);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-500/15 flex-shrink-0">
            <i className="ri-customer-service-2-fill text-blue-400 text-xl"></i>
          </div>
          <div>
            <h2 className="text-white font-semibold text-base">Canlı Destek Widgeti</h2>
            <p className="text-[#7B8FAF] text-xs">Sağ altta açılır destek butonu</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#7B8FAF] text-xs">{data.enabled ? "Aktif" : "Kapalı"}</span>
          <button onClick={() => upd("enabled", !data.enabled)}
            className={`relative w-11 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${data.enabled ? "bg-blue-500" : "bg-[#2B3654]"}`}>
            <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all" style={{ left: data.enabled ? "22px" : "2px" }}></span>
          </button>
        </div>
      </div>

      <div className={`space-y-4 transition-opacity ${data.enabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>

        {/* Agent Photo */}
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Temsilci Profil Fotoğrafı</label>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#2B3654] flex-shrink-0 bg-[#0F1729]">
              <img src={data.agentImageUrl} alt="Temsilci" className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
            <label className="flex-1 flex items-center gap-3 cursor-pointer border border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-4 py-3 transition-colors">
              <div className="w-8 h-8 flex items-center justify-center bg-[#3B82F6]/20 rounded-full flex-shrink-0">
                {uploadingPhoto
                  ? <i className="ri-loader-4-line text-[#3B82F6] animate-spin"></i>
                  : <i className="ri-upload-cloud-2-line text-[#3B82F6]"></i>}
              </div>
              <div>
                <p className="text-white text-xs font-medium">{uploadingPhoto ? "Yükleniyor..." : "Fotoğraf Değiştir"}</p>
                <p className="text-[#7B8FAF] text-xs">PNG, JPG — kare format önerilir</p>
              </div>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" disabled={uploadingPhoto}
                onChange={e => e.target.files?.[0] && handleAgentPhotoUpload(e.target.files[0])} />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Widget Başlığı</label>
            <input type="text" value={data.title} onChange={e => upd("title", e.target.value)}
              className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
          </div>
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Alt Başlık</label>
            <input type="text" value={data.subtitle} onChange={e => upd("subtitle", e.target.value)}
              className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
          </div>
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Temsilci Adı</label>
            <input type="text" value={data.agentName} onChange={e => upd("agentName", e.target.value)}
              className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
          </div>
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Çalışma Saatleri</label>
            <input type="text" value={data.onlineHours} onChange={e => upd("onlineHours", e.target.value)}
              placeholder="Hafta içi 09:00 - 18:00"
              className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
          </div>
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Mesaj / Yönlendirme Metni</label>
          <textarea value={data.offlineMessage} onChange={e => upd("offlineMessage", e.target.value)} rows={2}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] resize-none" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Mod</label>
          <div className="flex gap-3">
            <button onClick={() => upd("formEnabled", true)}
              className={`flex-1 py-2.5 text-sm rounded-lg border transition-all cursor-pointer whitespace-nowrap ${data.formEnabled ? "bg-[#3B82F6]/20 border-[#3B82F6]/60 text-white" : "border-[#2B3654] text-[#7B8FAF] hover:text-white"}`}>
              <i className="ri-file-list-3-line mr-2"></i>İletişim Formu
            </button>
            <button onClick={() => upd("formEnabled", false)}
              className={`flex-1 py-2.5 text-sm rounded-lg border transition-all cursor-pointer whitespace-nowrap ${!data.formEnabled ? "bg-[#3B82F6]/20 border-[#3B82F6]/60 text-white" : "border-[#2B3654] text-[#7B8FAF] hover:text-white"}`}>
              <i className="ri-message-3-line mr-2"></i>Chat Simülasyonu
            </button>
          </div>
          <p className="text-[#465886] text-xs mt-1.5">
            {data.formEnabled ? "Ziyaretçi form doldurup mesaj gönderir, mesajlar panelde görünür." : "Simüle chatbot yanıtları verir."}
          </p>
        </div>
      </div>

      <button onClick={handleSave}
        className={`w-full py-2.5 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
        {saved ? "✓ Kaydedildi" : "Canlı Destek Ayarlarını Kaydet"}
      </button>
    </div>
  );
}

// ── Main Export ──────────────────────────────────────────────────────────────

type CommsTab = "whatsapp" | "livechat" | "schedule" | "notif";

export default function CommunicationSettings() {
  const [activeTab, setActiveTab] = useState<CommsTab>("whatsapp");

  const tabs: { id: CommsTab; label: string; icon: string }[] = [
    { id: "whatsapp",  label: "WhatsApp",    icon: "ri-whatsapp-line" },
    { id: "livechat", label: "Canlı Destek", icon: "ri-customer-service-2-line" },
    { id: "schedule", label: "Zamanlama",    icon: "ri-time-line" },
    { id: "notif",    label: "Bildirim Sesi", icon: "ri-notification-3-line" },
  ];

  return (
    <div className="space-y-5">
      <div className="pb-4 border-b border-[#2B3654]">
        <h2 className="text-white font-semibold text-base">İletişim Widgetları</h2>
        <p className="text-[#7B8FAF] text-xs mt-0.5">Site ziyaretçileriyle hızlı iletişim butonları</p>
      </div>
      <div className="flex gap-2 bg-[#0F1729] border border-[#2B3654] rounded-xl p-1.5 flex-wrap">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-w-[80px] ${activeTab === t.id ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white"}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${t.icon} text-sm`}></i>
            </div>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>
      {activeTab === "whatsapp" && <WhatsAppSection />}
      {activeTab === "livechat" && <LiveChatSection />}
      {activeTab === "schedule" && <ChatScheduleSection />}
      {activeTab === "notif" && <NotificationSoundSection />}
    </div>
  );
}
